/**
 * CadDocument — the facade the UI talks to.
 *
 * Wires together: Timeline (immutable state) + DocumentHistory (undo/redo) +
 * TimelineEvaluator (incremental regen) + autosave to IndexedDB.
 *
 * Every user gesture becomes: build new Timeline → commit(label) → the
 * document schedules an async re-evaluation and notifies subscribers with the
 * report. Evaluation is debounced through the evaluator's generation counter,
 * so rapid edits (dragging a push/pull handle) never queue up stale kernel
 * work.
 */

import type { GeomKernel } from './geometry/types';
import { resolveMaterialChain, type MaterialDef } from './materials';
import { db, type ProjectRecord } from './storage/db';
import {
  type EvaluationReport,
  type PartSource,
  TimelineEvaluator,
} from './timeline/evaluator';
import { hashValue } from './timeline/hash';
import { DocumentHistory } from './timeline/history';
import { Timeline } from './timeline/timeline';
import type { FeatureId } from './timeline/types';

export type DocumentListener = (doc: CadDocument, report: EvaluationReport | null) => void;

export class CadDocument {
  private history: DocumentHistory;
  private evaluator: TimelineEvaluator;
  private listeners = new Set<DocumentListener>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Uncommitted timeline shown during interactive drags (push/pull). */
  private pendingPreview: Timeline | null = null;
  lastReport: EvaluationReport | null = null;

  constructor(
    public readonly id: string,
    public name: string,
    private readonly kernel: GeomKernel,
    initial: Timeline = Timeline.empty(),
  ) {
    this.history = new DocumentHistory(initial);
    // Assemblies: part features load sibling projects from IndexedDB; the
    // stack seeds the cycle guard with THIS project's id.
    const resolvePart = async (projectId: string): Promise<PartSource | null> => {
      const rec = await db.projects.get(projectId);
      if (!rec) return null;
      // Hash with material fields stripped: a material edit in the source
      // project must not re-execute every assembly that inserts it (the
      // no-rebuild invariant crosses project boundaries too).
      const stripped = {
        ...rec.timeline,
        features: rec.timeline.features.map(
          ({ material: _m, materialColor: _c, ...rest }) => rest,
        ),
      };
      return { timeline: rec.timeline, contentHash: hashValue(stripped) };
    };
    this.evaluator = new TimelineEvaluator(kernel, resolvePart, [id]);
  }

  static async open(id: string, kernel: GeomKernel): Promise<CadDocument | undefined> {
    const rec = await db.projects.get(id);
    if (!rec) return undefined;
    return new CadDocument(id, rec.name, kernel, Timeline.fromJSON(rec.timeline));
  }

  get timeline(): Timeline {
    return this.pendingPreview ?? this.history.timeline;
  }

  get canUndo(): boolean {
    return this.history.canUndo;
  }

  get canRedo(): boolean {
    return this.history.canRedo;
  }

  subscribe(fn: DocumentListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Apply a timeline operation. `mutate` receives the current timeline and
   * returns the next one (throwing TimelineError leaves state untouched).
   */
  async apply(label: string, mutate: (t: Timeline) => Timeline): Promise<EvaluationReport> {
    this.pendingPreview = null;
    const next = mutate(this.history.timeline);
    this.history.commit(next, label);
    return this.reevaluate();
  }

  /** Coalescing state: drag storms collapse to ≤1 in-flight kernel pass. */
  private previewBusy = false;
  private previewAgain = false;
  private previewSettled: Promise<EvaluationReport> | null = null;

  /**
   * Interactive preview: evaluate `next` and notify subscribers WITHOUT
   * touching undo history or autosave. Call as often as the pointer moves —
   * passes COALESCE: while one evaluation is in flight, newer timelines just
   * replace the pending one, and a single trailing pass picks up the latest.
   * Without this, a 2-second drag queues ~120 kernel round-trips that keep
   * draining for seconds after release. The returned promise resolves with
   * a report at least as new as this call's timeline.
   */
  async preview(next: Timeline): Promise<EvaluationReport> {
    this.pendingPreview = next;
    if (this.previewBusy) {
      this.previewAgain = true;
      return this.previewSettled!;
    }
    this.previewBusy = true;
    this.previewSettled = (async () => {
      try {
        let report: EvaluationReport;
        do {
          this.previewAgain = false;
          report = await this.reevaluate();
        } while (this.previewAgain && this.pendingPreview !== null);
        return report;
      } finally {
        // Must release the gate even if a pass throws (e.g. a listener
        // errors) — otherwise previewBusy sticks true and every future
        // preview returns this poisoned promise, freezing all dragging.
        this.previewBusy = false;
      }
    })();
    return this.previewSettled;
  }

  /** Commit the current preview as one undo step (drag release). */
  async commitPreview(label: string): Promise<void> {
    const next = this.pendingPreview;
    this.pendingPreview = null;
    if (next) {
      this.history.commit(next, label);
      this.scheduleSave();
    }
  }

  /** Abandon the preview (Escape mid-drag) and restore committed state. */
  async cancelPreview(): Promise<void> {
    if (!this.pendingPreview) return;
    this.pendingPreview = null;
    await this.reevaluate();
  }

  /** Evaluate + notify without any edit (initial load). */
  async refresh(): Promise<EvaluationReport> {
    return this.reevaluate();
  }

  async undo(): Promise<EvaluationReport | null> {
    return this.history.undo() ? this.reevaluate() : null;
  }

  async redo(): Promise<EvaluationReport | null> {
    return this.history.redo() ? this.reevaluate() : null;
  }

  private async reevaluate(): Promise<EvaluationReport> {
    const isPreview = this.pendingPreview !== null;
    const report = await this.evaluator.evaluate(this.timeline);
    // A concurrent evaluate() (a mid-drag apply/undo/cancel by the AI copilot
    // or another gesture) supersedes this pass — the evaluator abandoned it
    // and returned an INCOMPLETE report. Discarding it is always safe: the
    // newer pass runs its own reevaluate() and produces the authoritative
    // state. Applying it would clobber lastReport (poisoning mass/export) and
    // make the viewport dispose bodies missing from the partial report.
    if (report.generation !== this.evaluator.currentGeneration) {
      return this.lastReport ?? report;
    }
    this.lastReport = report;
    // Let stateful kernels (B-rep shape registry) drop deleted features.
    if (!isPreview) this.kernel.prune?.(this.timeline.features.map((f) => f.id));
    for (const fn of this.listeners) fn(this, report);
    if (!isPreview) this.scheduleSave();
    return report;
  }

  /** Resolve a feature's exact B-rep id, or null (mesh-only / consumed). */
  brepIdOf(id: FeatureId): string | null {
    const r = this.lastReport?.results.get(id);
    return r?.kind === 'body' ? (r.brepId ?? null) : null;
  }

  /**
   * A body's effective material/color: its own node's assignment, or the
   * nearest consumed ancestor's (a fillet/boolean inherits what it absorbed —
   * assignments survive downstream modeling). Material and color resolve
   * INDEPENDENTLY: a color-only override on a descendant tints the material
   * inherited from deeper in the chain, it does not replace it.
   */
  resolveMaterial(id: FeatureId): { material?: MaterialDef; color?: string } | null {
    return resolveMaterialChain(
      (fid) => this.timeline.get(fid as FeatureId),
      (fid) => this.lastReport?.results.get(fid as FeatureId),
      id,
    );
  }

  /**
   * A part feature's per-component source materials (null elsewhere).
   * RIGID consumers (mates, single-copy moves) preserve component volumes
   * exactly, so the walk follows them — a mixed-material part keeps summing
   * per-component mass after being placed in the assembly.
   */
  componentMaterialsOf(
    id: FeatureId,
  ): readonly { material?: string; color?: string; volume: number }[] | null {
    const seen = new Set<FeatureId>();
    let fid = id;
    while (!seen.has(fid)) {
      seen.add(fid);
      const r = this.lastReport?.results.get(fid);
      if (r?.kind !== 'body') return null;
      if (r.componentMaterials?.length) return r.componentMaterials;
      const node = this.timeline.get(fid);
      // A transform is volume-preserving ONLY as a plain move (copies===0);
      // copies>=1 is original + N copies (multiplied volume), so its cached
      // per-component volumes no longer match — don't walk through it.
      const copies = (node?.params as { copies?: number } | undefined)?.copies ?? 0;
      const rigid = node?.type === 'mate' || (node?.type === 'transform' && copies === 0);
      if (!rigid || r.consumed?.length !== 1) return null;
      fid = r.consumed[0]!;
    }
    return null;
  }

  /** Undo-stack depth — a checkpoint handle for rollbackTo(). */
  get historyDepth(): number {
    return this.history.depth;
  }

  /** Drop redo-able states (dry-run trials must not be redo-reachable). */
  discardRedo(): void {
    this.history.clearRedo();
  }

  /** Undo back to a checkpoint depth (no-op if already at/below it). */
  async rollbackTo(depth: number): Promise<number> {
    // A token below the trim floor points at a state the limit already
    // dropped — fail loudly instead of silently landing on the oldest survivor.
    if (depth < this.history.oldestReachableDepth) {
      throw new Error(
        'Checkpoint is older than the retained history (200-op limit) and can no longer be restored',
      );
    }
    let steps = 0;
    while (this.history.depth > depth && this.history.undo()) steps++;
    if (steps > 0) await this.reevaluate();
    return steps;
  }

  /**
   * STEP text of the given features' bodies (default: all visible B-rep
   * bodies), each named after its feature (XCAF products).
   */
  async exportStep(featureIds?: readonly FeatureId[]): Promise<string> {
    if (!this.kernel.exportStep) throw new Error('STEP export needs the B-rep kernel');
    const report = this.lastReport;
    const brepIds: string[] = [];
    const names: string[] = [];
    const wanted = featureIds ? new Set(featureIds) : null;
    if (report) {
      for (const [id, r] of report.results) {
        if (r.kind !== 'body' || !r.brepId) continue;
        if (wanted ? !wanted.has(id) : report.consumed.has(id) || r.mesh.positions.length === 0) continue;
        brepIds.push(r.brepId);
        names.push(this.timeline.get(id)?.name ?? id);
      }
    }
    if (brepIds.length === 0) throw new Error('No exact B-rep bodies to export');
    return this.kernel.exportStep(brepIds, names);
  }

  /** Exact mass properties of a feature's body (B-rep kernel only). */
  async massProps(
    id: FeatureId,
  ): Promise<{ volume: number; area: number; centerOfMass: [number, number, number] }> {
    const brepId = this.brepIdOf(id);
    if (!brepId || !this.kernel.massProps) {
      throw new Error('Mass properties need an exact B-rep body');
    }
    return this.kernel.massProps(brepId);
  }

  /** Exact minimum distance between two features' bodies (witness points). */
  async distance(
    a: FeatureId,
    b: FeatureId,
  ): Promise<{ distance: number; pointA: [number, number, number]; pointB: [number, number, number] }> {
    const ba = this.brepIdOf(a);
    const bb = this.brepIdOf(b);
    if (!ba || !bb || !this.kernel.distance) {
      throw new Error('Distance needs two exact B-rep bodies');
    }
    return this.kernel.distance(ba, bb);
  }

  /** Measure the face/edge of a feature's body nearest a world point. */
  async measureNear(
    id: FeatureId,
    point: [number, number, number],
    want: 'auto' | 'face' | 'edge',
  ): Promise<import('./geometry/types').MeasureResult> {
    const brepId = this.brepIdOf(id);
    if (!brepId || !this.kernel.measureNear) throw new Error('measure needs an exact B-rep body');
    return this.kernel.measureNear(brepId, point, want);
  }

  /** HLR projection of the given brepIds along a view direction. */
  async projectViewOf(
    brepIds: readonly string[],
    dir: [number, number, number],
    xdir: [number, number, number],
  ): Promise<{ visible: Float32Array; hidden: Float32Array; circles: Float32Array; sectionSegs: Float32Array }> {
    if (!this.kernel.projectView) throw new Error('Projection needs the B-rep kernel');
    return this.kernel.projectView(brepIds, dir, xdir);
  }

  /** Topology census of a feature's body. */
  async topologySummary(id: FeatureId): Promise<import('./geometry/types').TopologySummary> {
    const brepId = this.brepIdOf(id);
    if (!brepId || !this.kernel.topologySummary) {
      throw new Error('Topology summary needs an exact B-rep body');
    }
    return this.kernel.topologySummary(brepId);
  }

  /** Sampled wall-thickness distribution of a feature's body. */
  async wallThickness(
    id: FeatureId,
    samples = 200,
  ): Promise<import('./geometry/types').WallThicknessResult> {
    const brepId = this.brepIdOf(id);
    if (!brepId || !this.kernel.wallThickness) {
      throw new Error('Wall thickness needs an exact B-rep body');
    }
    return this.kernel.wallThickness(brepId, samples);
  }

  /** Plane∩body cut segments (2D, in the section plane) for a brepId. */
  async crossSectionOf(
    brepId: string,
    axis: 'X' | 'Y' | 'Z',
    offset: number,
  ): Promise<Float32Array> {
    if (!this.kernel.crossSection) throw new Error('Cross-section needs the B-rep kernel');
    return this.kernel.crossSection(brepId, axis, offset);
  }

  /** Exact overlap volume between two features' bodies (0 = clear). */
  async interference(a: FeatureId, b: FeatureId): Promise<number> {
    const ba = this.brepIdOf(a);
    const bb = this.brepIdOf(b);
    if (!ba || !bb || !this.kernel.interference) {
      throw new Error('Interference check needs two exact B-rep bodies');
    }
    return this.kernel.interference(ba, bb);
  }

  /** Debounced autosave of the parametric recipe (small, cheap). */
  private scheduleSave(): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.save();
    }, 500);
  }

  /** Rename + persist. */
  async rename(name: string): Promise<void> {
    this.name = name.trim() || this.name;
    await this.save();
  }

  /** Flush any pending debounced autosave immediately (project switches). */
  async flushSave(): Promise<void> {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    await this.save();
  }

  async save(): Promise<void> {
    const existing = await db.projects.get(this.id);
    const record: ProjectRecord = {
      id: this.id,
      name: this.name,
      createdAt: existing?.createdAt ?? Date.now(),
      updatedAt: Date.now(),
      timeline: this.timeline.toJSON(),
    };
    // Thumbnails are written separately (captureThumbnail) — preserve them.
    if (existing?.thumbnail) record.thumbnail = existing.thumbnail;
    await db.projects.put(record);
  }
}
